import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Pencil, X, Search, Star, Lock, Eye, EyeOff, Upload } from "lucide-react";
import api from "../../lib/api";
import SafeImage from "../../components/SafeImage";

const CATEGORIES = [
  { v: "finance", l: "Finance" },
  { v: "politique", l: "Politique" },
  { v: "socio-culturel", l: "Socio-culturel" },
  { v: "interview", l: "Interview" },
  { v: "top", l: "Top" },
  { v: "sport", l: "Sport" },
  { v: "actualite", l: "Actualité" },
  { v: "economie", l: "Économie" },
  { v: "societe", l: "Société" },
];

const eur = (n, c = "EUR") =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: c, maximumFractionDigits: 0 }).format(n || 0);

function ArticleForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState({
    title_fr: initial?.title?.fr || "",
    title_en: initial?.title?.en || "",
    excerpt_fr: initial?.excerpt?.fr || "",
    excerpt_en: initial?.excerpt?.en || "",
    body_fr: initial?.body?.fr || "",
    body_en: initial?.body?.en || "",
    category: initial?.category || "actualite",
    cover_url: initial?.cover_url || "",
    author: initial?.author || "Réel Mambe Mola",
    published: initial?.published !== false,
    featured: !!initial?.featured,
    is_premium: !!initial?.is_premium,
    price: initial?.price ?? 4,
    currency: initial?.currency || "EUR",
    preview_fr: initial?.preview_excerpt?.fr || "",
    preview_en: initial?.preview_excerpt?.en || "",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadCover = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", "cover");
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, cover_url: url }));
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: { fr: form.title_fr, en: form.title_en || form.title_fr },
        excerpt: { fr: form.excerpt_fr, en: form.excerpt_en || form.excerpt_fr },
        body: { fr: form.body_fr, en: form.body_en || form.body_fr },
        category: form.category,
        cover_url: form.cover_url || null,
        author: form.author,
        published: !!form.published,
        featured: !!form.featured,
        is_premium: !!form.is_premium,
        price: parseFloat(form.price) || 4,
        currency: form.currency,
        preview_excerpt: { fr: form.preview_fr, en: form.preview_en || form.preview_fr },
      };
      if (initial?.id) await api.put(`/articles/${initial.id}`, payload);
      else await api.post("/articles", payload);
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.72)" }}>
      <form onSubmit={submit} className="w-full max-w-4xl gai-card gai-sharp p-7 relative max-h-[92vh] overflow-y-auto" data-testid="article-form">
        <button type="button" onClick={onCancel} className="absolute top-4 right-4 text-[var(--gai-grey-600)] hover:text-[var(--gai-red)]" aria-label="Fermer">
          <X size={18} />
        </button>
        <div className="overline mb-1">{initial?.id ? "Modifier" : "Nouvel"}</div>
        <h3 className="serif text-2xl text-[var(--gai-black)] mb-5">Article</h3>

        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="overline mb-1">Titre FR *</div>
              <input required className="gai-input" placeholder="Titre en français"
                value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })}
                data-testid="article-title-fr" />
            </div>
            <div>
              <div className="overline mb-1">Title EN</div>
              <input className="gai-input" placeholder="Title in English (optional)"
                value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })}
                data-testid="article-title-en" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="overline mb-1">Chapeau FR</div>
              <textarea rows={3} className="gai-input resize-none" placeholder="Résumé / accroche"
                value={form.excerpt_fr} onChange={(e) => setForm({ ...form, excerpt_fr: e.target.value })}
                data-testid="article-excerpt-fr" />
            </div>
            <div>
              <div className="overline mb-1">Excerpt EN</div>
              <textarea rows={3} className="gai-input resize-none"
                value={form.excerpt_en} onChange={(e) => setForm({ ...form, excerpt_en: e.target.value })} />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="overline mb-1">Corps FR *</div>
              <textarea required rows={10} className="gai-input resize-y" placeholder="Contenu complet (multi-paragraphes)"
                value={form.body_fr} onChange={(e) => setForm({ ...form, body_fr: e.target.value })}
                data-testid="article-body-fr" />
            </div>
            <div>
              <div className="overline mb-1">Body EN</div>
              <textarea rows={10} className="gai-input resize-y"
                value={form.body_en} onChange={(e) => setForm({ ...form, body_en: e.target.value })} />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <div className="overline mb-1">Catégorie</div>
              <select className="gai-input" value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="article-category">
                {CATEGORIES.map((c) => (
                  <option key={c.v} value={c.v} style={{ background: "var(--gai-white)", color: "var(--gai-black)" }}>{c.l}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="overline mb-1">Auteur</div>
              <input className="gai-input" placeholder="Réel Mambe Mola"
                value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })}
                data-testid="article-author" />
            </div>
            <div>
              <div className="overline mb-1">URL couverture (1200×630)</div>
              <input className="gai-input" placeholder="https://..."
                value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })}
                data-testid="article-cover-url" />
            </div>
          </div>

          <div className="flex items-center gap-4 flex-wrap p-4 border" style={{ borderColor: "var(--gai-grey-200)" }}>
            {form.cover_url && (
              <SafeImage src={form.cover_url} alt="cover" className="w-24 h-14 object-cover" style={{ width: 96, height: 56 }} />
            )}
            <label className="gai-btn-outline text-xs cursor-pointer" data-testid="article-upload-btn">
              <Upload size={12} /> {uploading ? "Envoi…" : "Uploader une image"}
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadCover(e.target.files[0])} />
            </label>
            <span className="text-xs text-[var(--gai-grey-500)]">
              Format recommandé : <b>1200×630 px</b> (16:9) — JPG/PNG/WEBP, &lt;5 Mo
            </span>
          </div>

          <div className="grid md:grid-cols-3 gap-4 p-4 border" style={{ borderColor: "var(--gai-grey-200)" }}>
            <label className="flex items-center gap-2 text-sm text-[var(--gai-grey-800)]">
              <input type="checkbox" checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                data-testid="article-published" />
              <Eye size={13} /> Publié
            </label>
            <label className="flex items-center gap-2 text-sm text-[var(--gai-grey-800)]">
              <input type="checkbox" checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                data-testid="article-featured" />
              <Star size={13} /> À la une
            </label>
            <label className="flex items-center gap-2 text-sm text-[var(--gai-grey-800)]">
              <input type="checkbox" checked={form.is_premium}
                onChange={(e) => setForm({ ...form, is_premium: e.target.checked })}
                data-testid="article-premium" />
              <Lock size={13} /> Premium (payant)
            </label>
          </div>

          {form.is_premium && (
            <div className="grid md:grid-cols-3 gap-3 p-4 border" style={{ borderColor: "var(--gai-red)", background: "rgba(227,30,36,0.04)" }}>
              <div>
                <div className="overline mb-1">Prix</div>
                <input type="number" min="0" step="0.5" className="gai-input"
                  value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                  data-testid="article-price" />
              </div>
              <div>
                <div className="overline mb-1">Devise</div>
                <select className="gai-input" value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                  {["EUR", "USD", "XOF", "XAF", "CDF", "MAD", "GBP"].map((c) =>
                    <option key={c} value={c} style={{ background: "var(--gai-white)", color: "var(--gai-black)" }}>{c}</option>)}
                </select>
              </div>
              <div className="md:col-span-1">
                <div className="overline mb-1">Extrait teaser FR</div>
                <textarea rows={2} className="gai-input resize-none" placeholder="Aperçu visible avant achat"
                  value={form.preview_fr} onChange={(e) => setForm({ ...form, preview_fr: e.target.value })} />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-5 mt-5 border-t" style={{ borderColor: "var(--gai-grey-200)" }}>
          <button type="button" onClick={onCancel} className="gai-btn-ghost text-xs">Annuler</button>
          <button disabled={busy} className="gai-btn-gold text-xs" data-testid="article-submit">
            {busy ? "Enregistrement…" : (initial?.id ? "Enregistrer" : "Publier l'article")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminArticles() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | article
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const refresh = async () => {
    setLoading(true);
    const r = await api.get("/admin/articles").catch(() => ({ data: [] }));
    setItems(r.data || []);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    return items.filter((a) => {
      if (filter === "premium" && !a.is_premium) return false;
      if (filter === "draft" && a.published) return false;
      if (filter === "published" && !a.published) return false;
      if (filter !== "all" && filter !== "premium" && filter !== "draft" && filter !== "published" && a.category !== filter) return false;
      if (q) {
        const t = (a.title?.fr || "") + " " + (a.title?.en || "") + " " + (a.author || "");
        if (!t.toLowerCase().includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [items, filter, q]);

  const remove = async (a) => {
    if (!window.confirm(`Supprimer définitivement l'article « ${a.title?.fr || "sans titre"} » ?`)) return;
    await api.delete(`/articles/${a.id}`);
    refresh();
  };

  const togglePublish = async (a) => {
    await api.put(`/articles/${a.id}`, {
      title: a.title, excerpt: a.excerpt || {}, body: a.body || {},
      category: a.category, cover_url: a.cover_url, author: a.author,
      published: !a.published, featured: !!a.featured,
      is_premium: !!a.is_premium, price: a.price ?? 4, currency: a.currency || "EUR",
      preview_excerpt: a.preview_excerpt || {},
    });
    refresh();
  };

  return (
    <div data-testid="page-admin-articles">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="overline mb-2">Rédaction</div>
          <h1 className="serif text-4xl md:text-5xl text-[var(--gai-black)] leading-none">Articles</h1>
          <p className="mt-2 text-sm text-[var(--gai-grey-500)] max-w-2xl">
            Ajoutez manuellement les articles issus de la page Facebook (ou d&apos;ailleurs), gérez les brouillons, marquez à la une et activez la vente premium.
          </p>
        </div>
        <button onClick={() => setEditing("new")} className="gai-btn-gold text-xs" data-testid="btn-new-article">
          <Plus size={12} /> Nouvel article
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gai-grey-500)]" />
          <input
            className="gai-input pl-9"
            style={{ paddingLeft: "2.25rem" }}
            placeholder="Rechercher un titre, un auteur…"
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="articles-search"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="articles-filters">
        {[
          { v: "all", l: `Toutes (${items.length})` },
          { v: "published", l: `Publiés (${items.filter(a => a.published).length})` },
          { v: "draft", l: `Brouillons (${items.filter(a => !a.published).length})` },
          { v: "premium", l: `Premium (${items.filter(a => a.is_premium).length})` },
          ...CATEGORIES.filter(c => items.some(a => a.category === c.v)).map(c => ({ v: c.v, l: c.l })),
        ].map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)}
            className={`px-3 py-2 text-[0.7rem] tracking-[0.18em] uppercase border ${
              filter === f.v
                ? "bg-[var(--gai-red)] text-[var(--gai-white)] border-[var(--gai-red)]"
                : "border-[var(--gai-grey-200)] text-[var(--gai-black)] hover:border-[var(--gai-red)]"
            }`}
            data-testid={`articles-filter-${f.v}`}>
            {f.l}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-[var(--gai-grey-500)]">Chargement…</div>}

      {!loading && filtered.length === 0 && (
        <div className="gai-card gai-sharp p-10 text-center text-[var(--gai-grey-500)]">
          Aucun article. Créez le premier.
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((a) => (
          <div key={a.id} className="gai-card gai-sharp overflow-hidden flex flex-col" data-testid={`article-card-${a.id}`}>
            <SafeImage src={a.cover_url} alt="" className="w-full h-40 object-cover" style={{ width: "100%", height: 160 }} />
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="overline text-[var(--gai-red)]">{a.category}</span>
                {!a.published && <span className="text-[0.6rem] tracking-[0.18em] uppercase text-[var(--gai-grey-500)]">Brouillon</span>}
                {a.featured && <span className="text-[0.6rem] tracking-[0.18em] uppercase text-[var(--gai-black)]"><Star size={10} className="inline" /> Une</span>}
                {a.is_premium && <span className="text-[0.6rem] tracking-[0.18em] uppercase text-[var(--gai-red)]"><Lock size={10} className="inline" /> {eur(a.price, a.currency)}</span>}
              </div>
              <div className="serif text-base text-[var(--gai-black)] leading-tight line-clamp-2">{a.title?.fr || "Sans titre"}</div>
              <div className="text-[0.7rem] mt-1 text-[var(--gai-grey-500)]">Par {a.author}</div>
              {a.excerpt?.fr && <p className="mt-2 text-xs text-[var(--gai-grey-600)] line-clamp-2 flex-1">{a.excerpt.fr}</p>}

              <div className="mt-4 pt-3 border-t flex items-center justify-between gap-2" style={{ borderColor: "var(--gai-grey-200)" }}>
                <button onClick={() => togglePublish(a)} className="gai-btn-ghost text-[0.7rem]" data-testid={`article-toggle-${a.id}`}>
                  {a.published ? <><EyeOff size={12} /> Dépublier</> : <><Eye size={12} /> Publier</>}
                </button>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(a)} className="gai-btn-ghost p-2" aria-label="Modifier" data-testid={`article-edit-${a.id}`}>
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => remove(a)} className="gai-btn-ghost p-2 text-[var(--gai-red)] hover:opacity-80" aria-label="Supprimer" data-testid={`article-delete-${a.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <ArticleForm
          initial={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}
